import { ShieldCheck, Sun, Waves } from "lucide-react";
import { useSiteData } from "@/context/SiteDataContext";
import { ActivityGrid } from "@/components/site/WaterActivities";
import { BookingEnquiry } from "@/components/site/BookingEnquiry";
import { PageHero, SectionHead } from "@/components/site/PageHero";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { waLink } from "@/lib/api";
import { Seo, breadcrumb } from "@/components/site/Seo";

const TIPS = [
  { icon: ShieldCheck, t: "Safety first", d: "Activities are run by trained operators with safety briefings and equipment." },
  { icon: Sun, t: "Best in calm weather", d: "Water activities depend on sea and weather conditions on the day." },
  { icon: Waves, t: "Combine with your trip", d: "Add activities to any package, hotel or ferry booking with us." },
];

export default function WaterActivitiesPage() {
  const { settings } = useSiteData();
  return (
    <div data-testid="water-activities-page">
      <Seo title="Andaman Water Activities | Scuba Diving, Snorkelling & Sea Walk | Ocean Wings Travel"
        description="Scuba diving at Havelock, snorkelling at Elephant Beach, sea walk at North Bay, glass-bottom boat rides and more water sports in the Andaman Islands. Add them to any trip."
        keywords="Andaman water activities, scuba diving Havelock, snorkelling Elephant Beach, sea walk North Bay, Andaman water sports, glass bottom boat Andaman"
        path="/water-activities" image={`${process.env.REACT_APP_SITE_URL}/activities/scuba.jpg`}
        jsonLd={[breadcrumb([["Home", "/"], ["Water Activities", "/water-activities"]])]} />
      <PageHero img="/activities/scuba.jpg" alt="Scuba diver among tropical fish" eyebrow="Andaman Water Activities" testId="water-hero"
        title="Water activities in the Andaman Islands"
        subtitle="Scuba diving, snorkeling, sea walk, parasailing, jet skiing, glass bottom boat, kayaking and banana boat rides, arranged by Ocean Wings Travel.">
        <a href={waLink(settings.whatsapp, "Hi Ocean Wings Travel, I'd like to book water activities in Andaman. Activities: , Date: , Persons: ")} target="_blank" rel="noreferrer" data-testid="water-hero-whatsapp"
          className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors"><WhatsAppIcon className="h-5 w-5" /> Enquire on WhatsApp</a>
      </PageHero>
      <section className="container-x pt-20">
        <SectionHead eyebrow="Choose your adventure" title="8 ways to enjoy the Andaman Sea" subtitle="Tell us the activities you'd like and your travel dates. We'll confirm availability and details." />
        <div className="mt-10"><ActivityGrid /></div>
      </section>
      <section className="container-x pt-20">
        <div className="rounded-3xl bg-mist p-6 sm:p-10 grid gap-8 md:grid-cols-3">
          {TIPS.map(({ icon: Icon, t, d }) => (
            <div key={t}><span className="h-11 w-11 rounded-xl bg-white text-ocean grid place-items-center shadow-sm"><Icon className="h-5 w-5" /></span>
              <p className="mt-3 font-semibold text-navy">{t}</p><p className="mt-1 text-sm text-slate-600">{d}</p></div>
          ))}
        </div>
      </section>
      <BookingEnquiry id="activity-enquiry" idPrefix="activity-enquiry" title="Book your water activities" tripType="Water activities"
        message="Water activities enquiry - Activities: , Island: , Date: , Persons: "
        wa="Hi Ocean Wings Travel, I'd like to book water activities in Andaman. Activities: , Date: , Persons: " />
    </div>
  );
}
